import React, { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../config/theme';
import PageLayout from './PageLayout';
import CloseButton from './Modal/CloseButton';
import Media from './Media/Media';
import ModalMedia from './Media/ModalMedia';

// PageContainer removed - handled by PageLayout component

const GalleryContainer = styled(motion.section)`
  padding: 100px ${theme.spacing.sectionPadding.horizontal} ${theme.spacing.sectionPadding.vertical};
  background-color: ${theme.colors.primary.background};
  position: relative;
  overflow: hidden;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-top: ${theme.spacing.md}; /* On mobile, PageTitle is static so less padding needed */
  }
`;

// Title removed - handled by PageLayout component

const Gallery = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 40px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 15px;
    margin-bottom: 30px;
  }
`;

const ImageContainer = styled(motion.div)`
  overflow: hidden;
  border: 1px solid ${theme.shadows.light};
  aspect-ratio: 4/3;
  cursor: pointer;
  transition: ${theme.transitions.default};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px ${theme.shadows.medium};
  }
`;

const MediaWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: block;
  transition: ${theme.transitions.default};
  
  ${ImageContainer}:hover & {
    transform: scale(1.05);
  }
`;

const Description = styled(motion.p)`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${theme.colors.primary.text};
  text-align: center;
  max-width: 600px;
  margin: 0 auto 40px;
  opacity: 0.8;
`;

// Modal for full-size image viewing
const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${theme.shadows.overlay.darkest};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: ${theme.spacing.lg};
  box-sizing: border-box;
`;


const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: ${theme.colors.primary.background};
  border: 1px solid ${theme.colors.primary.buttons};
  color: ${theme.colors.primary.buttons};
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  transition: ${theme.transitions.default};
  z-index: 2001;
  
  ${props => props.$left ? 'left: 30px;' : 'right: 30px;'}
  
  &:hover {
    background: ${theme.colors.primary.buttons};
    color: ${theme.colors.primary.background};
    transform: translateY(-50%) scale(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: ${theme.colors.primary.background};
      color: ${theme.colors.primary.buttons};
      transform: translateY(-50%);
    }
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const SwipeableModalImage = styled(motion.div)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const GalleryPage = ({ 
  title, 
  description, 
  images, 
  backButtonText, 
  backButtonLink,
  includeFooter = true 
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(null);

  // Scroll-to-top removed - handled globally by ScrollToTop component in App.js

  // Convert images to consistent format if needed
  const galleryImages = images.map((image, index) => {
    if (typeof image === 'string') {
      return { src: image, alt: `Gallery Image ${index + 1}` };
    }
    return image;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0, // No stagger - all elements animate simultaneously
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const handleCloseModal = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedImageIndex(null);
    }
  };

  const handlePrevImage = useCallback(() => {
    setSelectedImageIndex(prev => 
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  }, [galleryImages.length]);

  const handleNextImage = useCallback(() => {
    setSelectedImageIndex(prev => 
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
  }, [galleryImages.length]);

  const handleSwipe = (event, info) => {
    const swipeThreshold = 50;
    
    if (Math.abs(info.offset.x) > swipeThreshold) {
      if (info.offset.x > 0) {
        // Swiped right - go to previous image
        handlePrevImage();
      } else {
        // Swiped left - go to next image
        handleNextImage();
      }
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setSelectedImageIndex(null);
    } else if (e.key === 'ArrowLeft') {
      handlePrevImage();
    } else if (e.key === 'ArrowRight') {
      handleNextImage();
    }
  }, [handlePrevImage, handleNextImage]);

  useEffect(() => {
    if (selectedImageIndex !== null) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImageIndex, handleKeyDown]);

  return (
    <>
      <PageLayout
        title={title}
        backButtonText={backButtonText}
        backButtonLink={backButtonLink}
        includeFooter={includeFooter}
      >
        <GalleryContainer
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {description && (
            <Description variants={itemVariants}>
              {description}
            </Description>
          )}
          
          <Gallery variants={itemVariants}>
            {galleryImages.map((image, index) => (
              <ImageContainer
                key={index}
                onClick={() => handleImageClick(index)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 1 }} 
                animate={{ opacity: 1 }} 
              >
                <MediaWrapper>
                  <Media src={image.src} alt={image.alt} />
                </MediaWrapper>
              </ImageContainer>
            ))}
          </Gallery>
        </GalleryContainer>
      </PageLayout>

      {/* Modal for full-size image viewing */}
      {selectedImageIndex !== null && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleCloseModal}
        >
          <SwipeableModalImage
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleSwipe}
            whileDrag={{ cursor: "grabbing" }}
          >
            <ModalMedia 
              src={galleryImages[selectedImageIndex].src} 
              alt={galleryImages[selectedImageIndex].alt} 
            />
          </SwipeableModalImage>
          
          {/* Navigation arrows - hidden on mobile/tablet */}
          <NavButton 
            $left
            onClick={handlePrevImage}
            disabled={galleryImages.length <= 1}
          >
            ‹
          </NavButton>
          
          <NavButton 
            onClick={handleNextImage}
            disabled={galleryImages.length <= 1}
          >
            ›
          </NavButton>
          
          <CloseButton 
            onClick={() => setSelectedImageIndex(null)}
            $top="30px"
            $right="30px"
            $transform="none"
            $size="40px"
            $borderWidth="1px"
            $fontSize="1.2rem"
            $zIndex={2001}
          >
            ×
          </CloseButton>
        </Modal>
      )}
    </>
  );
};

export default GalleryPage;